"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.upload = void 0;
const upload_attach_to_S3_1 = require("./private/upload_attach_to_S3");
const core_1 = require("../core");
const lodash_1 = require("lodash");
/**
 * An upload file method that can be used to upload a file and go through the attachment field writing.
 *
 * @param params.file Files to be uploaded.
 * @param params.datasheetId Upload files to datasheet.
 * @param params.onProgress File upload progress callback method.
 * @returns
 *
 * ### Example
 * ``` ts
 * import React, { useState } from 'react';
 * import { upload, useDatasheet } from '@apitable/widget-sdk';
 *
 * function UploadFile() {
 *   const datasheet = useDatasheet();
 *   const [progress, setProgress] = useState(0);
 *   const [error, setError] = useState();
 *   function uploadFile(file) {
 *     datasheet && upload({
 *       file,
 *       datasheetId: datasheet?.datasheetId,
 *       onProgress: ({ loaded, total }) => {
 *         setProgress(loaded / total);
 *       }
 *     }).then(response => {
 *     const valuesMap = { fldGi8tYQfXcc: [response] };
 *     const permission = datasheet.checkPermissionsForAddRecord(valuesMap);
 *       if (permission.acceptable) {
 *         datasheet.addRecord(valuesMap);
 *       } else {
 *         setError(permission.message);
 *       }
 *     });
 *   }
 *   return (
 *     <div>
 *       <input type="file" onChange={(e) => {
 *         e.target.files?.[0] && uploadFile(e.target.files[0])
 *       }}/>
 *       <p>progress: {progress}</p>
 *       { error && <p>error: {error}</p>}
 *     </div>
 *   )
 * }
 * ```
 */
function upload(params) {
    const { file, datasheetId, onProgress } = params;
    return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
        if (!file || !datasheetId) {
            reject({ message: 'file or datasheetId is required' });
        }
        const res = yield (0, upload_attach_to_S3_1.uploadAttachToS3)({
            file,
            fileType: upload_attach_to_S3_1.UploadType.DstAttachment,
            nodeId: datasheetId,
            axiosConfig: {
                onUploadProgress: ({ loaded, total }) => {
                    onProgress && onProgress({ loaded, total });
                },
            },
        });
        const { success, data, message } = res.data;
        if (!success) {
            reject({
                message
            });
            return;
        }
        resolve((0, lodash_1.pickBy)({
            id: data.token,
            name: data.name || file.name,
            mimeType: data.mimeType,
            token: data.token,
            bucket: data.bucket,
            size: data.size,
            width: data.width,
            height: data.height,
            preview: data.preview,
            previewUrl: data.preview ? (0, core_1.cellValueToImageSrc)(data, { isPreview: true }) : undefined,
            url: (0, core_1.cellValueToImageSrc)(data),
        }, lodash_1.identity));
    }));
}
exports.upload = upload;
